import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { exportVisitorsCSV, getVisitors } from '../../../axios/Visitor/VisitorApi';
import { useTranslation } from 'react-i18next';
import { generateTranslateMuiTable } from '../../../utils/Function/commonFunction';
import { CustomAdvancedMenuList, StyledDataGrid } from '../../../components/StyledComponents';
import CustomPagination from '../CustomPagination';
import NoRowOverlay from '../../../assets/images/icons/NoRowOverlay';
import CustomColumnsPanel from '../../../components/CustomColumnsPanel';
import RemoveUserDialog from './RemoveUserDialog';
import DetailVisitorDialog from './DetailVisitorDialog';
import { useAuth } from '../../../context/AuthContext';
import dayjs from 'dayjs';
import { format, parseISO } from "date-fns";
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import AddIcon from '@mui/icons-material/Add';
import CustomSearch from '../../../components/CustomSearch';
import {
    ClickAwayListener,
    Grow,
    Link,
    MenuItem,
    Paper,
    Popper,
} from "@mui/material";
import ImportIcon20pxSvg from '../../../assets/images/icons/ImportIcon20pxSvg';
import ExportIcon16pxSvg from '../../../assets/images/icons/ExportIcon16pxSvg';
import AddVisitorUserDialog from './AddVisitorUserDialog';
import { LoadingProvider, useLoading } from '../../../context/LoadingContext';
import { toast } from 'react-toastify';
import CustomSplitButton from '../../../components/CustomSplitButton';
import ImportIcon16pxSvg from '../../../assets/images/icons/ImportIcon16pxSvg';

const VisitorUserGridInfoTable = ({ onRowClick, onTotalElementsChange, refreshData, handleRefreshData, refresh }) => {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [filteredRowCount, setFilteredRowCount] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const [removeUserOpen, setRemoveUserOpen] = useState(false);
    const [detailVisitorOpen, setDetailVisitorOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const { genaralSettingDateFormat } = useAuth();
    const [addVisitorUserOpen, setAddVisitorUserOpen] = useState(false);
    const [openAdvanced, setOpenAdvanced] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [totalElements, setTotalElements] = useState(0);
    const [show, hide] = useLoading();

    const anchorRefAdvanced = useRef(null);
    const [searchText, setSearchText] = useState("");

    const handleQuickFilterChange = (value) => {
        setSearchText(value);
    };
    const handleToggleAdvanced = () => {
        setOpenAdvanced((prev) => !prev);
    };
    const handleCloseAdvanced = (event) => {
        if (
            anchorRefAdvanced.current &&
            anchorRefAdvanced.current.contains(event.target)
        ) {
            return; // click vào button thì không close
        }
        setOpenAdvanced(false);
    };

    const handleCloseRemoveUser = () => {
        setRemoveUserOpen(false);
    };

    const handleOpenRemoveUser = () => {
        setRemoveUserOpen(true);
    };

    const tableTranslated = useMemo(() => generateTranslateMuiTable(t), [t]);
    const fetchData = async () => {
        const payload = {
            pageNo: page + 1,
            recordPerPage: pageSize,
            searchText: searchText || "",
            langId: localStorage.getItem("i18nextLngId"),
        };
        try {
            show();
            const response = await getVisitors(payload);

            if (response?.data?.responseCode === 0) {

                const list = response?.data?.data || [];

                setData(list);

                setTotalPages(
                    response?.data?.pagination?.totalPages || 1
                );

                const total =
                    response?.data?.pagination?.totalElements || 0;

                setTotalElements(total);
                onTotalElementsChange?.(total);

                // auto select first row
                if (list.length > 0) {
                    setSelectedRow(list[0]);
                    onRowClick?.(list[0]);
                } else {
                    setSelectedRow(null);
                    onRowClick?.(null);
                }
            } else {
                toast.error(
                    response?.data?.message ||
                    t("common.something-went-wrong")
                );
            }
        } catch (error) {

            console.error(error);

            toast.error(
                error?.response?.data?.message ||
                t("common.something-went-wrong")
            );

        } finally {
            hide();
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, pageSize, refreshData, refresh, searchText]);

    const columnData = [
        { field: 'fullName', headerName: t('common.full-name'), hideable: true, flex: 1, },
        { field: 'citizenIdentificationNumber', headerName: t('common.personal-id'), hideable: true, flex: 1, },
        { field: 'email', headerName: t('common.email'), hideable: true, flex: 1, },
    ];

    function getCurrentTimestamp() {
        const now = new Date();
        const yyyy = now.getFullYear();
        const MM = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const HH = String(now.getHours()).padStart(2, '0');
        const mm = String(now.getMinutes()).padStart(2, '0');
        const ss = String(now.getSeconds()).padStart(2, '0');
        return `${yyyy}${MM}${dd}${HH}${mm}${ss}`;
    }

    const exportCSV = async () => {
        setOpenAdvanced(false);
      
        const payload = {
          pageNo: totalPages,        // BE loop page 1 → totalPages (giống Report)
          recordPerPage: pageSize,
          searchText: searchText || "",
          langId: localStorage.getItem("i18nextLngId"),
        };
      
        try {
          show();
          const response = await exportVisitorsCSV(payload);
      
          const blob = new Blob([response.data], { type: 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
      
          const a = document.createElement('a');
          a.href = url;
          a.download = `${t('export.visitor-users')}_${getCurrentTimestamp()}.xlsx`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        } catch (error) {
          toast.error(error?.response?.data?.message || t('common.something-went-wrong'));
        } finally {
          hide();
        }
      };

    const handleRowsPerPageChange = (newPageSize) => {
        // newPageSize ở đây tuỳ CustomPagination, có thể là event hoặc number
        const size =
            typeof newPageSize === "number"
                ? newPageSize
                : parseInt(newPageSize.target.value, 10);

        setPage((prevPage) => {
            // index của record đầu tiên đang xem
            const firstRowIndex = prevPage * pageSize;

            // page mới sao cho vẫn chứa record đó
            const newPage = Math.floor(firstRowIndex / size);

            // phòng trường hợp vượt quá số trang
            const maxPage = Math.max(
                0,
                Math.ceil(filteredRowCount / size) - 1
            );

            return Math.min(newPage, maxPage);
        });

        setPageSize(size);
    };

    const handleRowClick = (params) => {
        const rowData = params.row;

        if (rowData) {
            setSelectedRow(rowData);
            setDetailVisitorOpen(true);

            onRowClick?.(rowData);
        }
    };

    return (
        <>
            <div className='flex flex-col gap-3'>
                <div className="flex items-center justify-between gap-4">
                    {/* LEFT ACTIONS */}
                    <CustomSplitButton
                        label={t("button.add")}
                        startIcon={<AddIcon />}
                        onMainClick={() => setAddVisitorUserOpen(true)}
                        onDropdownClick={handleToggleAdvanced}
                        ref={anchorRefAdvanced}
                    />

                    {/* SEARCH */}
                    <div className="w-full max-w-[300px]">
                        <CustomSearch
                            onSearchChange={handleQuickFilterChange}
                        />
                    </div>
                </div>

                <Fragment>
                    <StyledDataGrid
                        showColumnVerticalBorder
                        sx={{
                            height: "61vh",

                            ".MuiDataGrid-main": {
                                height: "61vh",
                            },

                            "& .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbar--horizontal":
                            {
                                display: "none !important",
                            },

                            "& .MuiDataGrid-scrollbarFiller": {
                                display: "none !important",
                            },
                            "& .MuiDataGrid-columnHeaders": {
                                paddingRight: 0,
                            },
                            "& .MuiDataGrid-topContainer": {
                                paddingRight: 0,
                            },

                            "&": {
                                "--DataGrid-scrollbarSize": "0px",
                            },

                            "& .MuiDataGrid-virtualScroller": {
                                scrollbarWidth: "none",
                            },
                            "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                                width: 0,
                                height: 0,
                            },
                            "&:hover .MuiDataGrid-virtualScroller, &:focus-within .MuiDataGrid-virtualScroller":
                            {
                                scrollbarWidth: "thin",
                            },
                            "&:hover .MuiDataGrid-virtualScroller::-webkit-scrollbar, &:focus-within .MuiDataGrid-virtualScroller::-webkit-scrollbar":
                            {
                                width: 10,
                                height: 10,
                            },

                            '& [class*="MuiDataGrid-scrollbar"][class*="--horizontal"]': {
                                display: "none !important",
                            },

                            "& input[type=number]::-webkit-inner-spin-button, \
 & input[type=number]::-webkit-outer-spin-button": {
                                WebkitAppearance: "none",
                                margin: 0,
                            },

                            "& input[type=number]": {
                                textAlign: "center",
                                MozAppearance: "textfield",
                                WebkitAppearance: "none",
                                padding: "5px !important",
                                height: "100%",
                                pointerEvents: "none",
                                caretColor: "transparent",
                                userSelect: "none",
                                paddingTop: "8px !important",
                                paddingBottom: "8px !important"
                            },
                        }}
                        rows={data || []}
                        rowCount={totalPages}
                        paginationMode="client"
                        columns={columnData}
                        // loading={gridLoading}
                        getRowId={(r) => r.id}
                        slots={{
                            pagination: CustomPagination,
                            noRowsOverlay: NoRowOverlay,
                            noResultsOverlay: NoRowOverlay,
                            columnsPanel: CustomColumnsPanel,
                        }}
                        slotProps={{
                            pagination: {
                                page,
                                count: totalElements,
                                rowsPerPage: pageSize,
                                onPageChange: setPage,
                                onRowsPerPageChange: handleRowsPerPageChange,
                            },
                        }}
                        localeText={tableTranslated}
                        onRowClick={handleRowClick} />
                </Fragment>
            </div>

            {removeUserOpen && <RemoveUserDialog id={selectedRow?.id} onClose={handleCloseRemoveUser} onRefresh={handleRefreshData} refreshData={refreshData} />}
            {/* {detailVisitorOpen && <DetailVisitorDialog id={selectedRow?.id} data={selectedRow} onClose={() => setDetailVisitorOpen(false)} onRefresh={handleRefreshData} refreshData={refreshData} />} */}
            {addVisitorUserOpen && <LoadingProvider><AddVisitorUserDialog onClose={() => setAddVisitorUserOpen(false)} onRefresh={handleRefreshData} refreshData={refreshData} /></LoadingProvider>}
            <Popper
                open={openAdvanced}
                anchorEl={anchorRefAdvanced.current}
                placement="bottom-start"
                transition
                disablePortal
                className="z-10"
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === "bottom" ? "left top" : "left bottom",
                        }}
                    >
                        <Paper style={{ borderRadius: "8px", }}>
                            <ClickAwayListener onClickAway={handleCloseAdvanced}>
                                <CustomAdvancedMenuList
                                    id="advanced-menu"
                                    aria-labelledby="advanced-button"
                                >
                                    <MenuItem
                                        style={{
                                            borderBottom: "1px solid #E5E7EB",
                                            opacity: "0.5",
                                            pointerEvents: "none",
                                        }}
                                    >
                                        <Link>
                                            <ImportIcon16pxSvg />
                                            <p>{t("button.import-data")}</p>
                                        </Link>
                                    </MenuItem>

                                    <MenuItem
                                        onClick={exportCSV}
                                        style={{
                                            borderBottom: "1px solid #E5E7EB",
                                        }}
                                    >
                                        <Link>
                                            <ExportIcon16pxSvg />
                                            <p>{t("button.export-data")}</p>
                                        </Link>
                                    </MenuItem>
                                </CustomAdvancedMenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    )
}

export default VisitorUserGridInfoTable